'use client';

// LedgerContext — React adapter over `LedgerStore`. The store owns the
// ref-counting, dedupe-by-id, cursor catch-up, and stable-reference logic;
// this file plumbs it into context + a hook AND installs the global
// wake/reconnect triggers (focus / visibilitychange / online /
// transport-reconnect / 2-min interval) per ADR 011 §5, ADR 017 §5, and
// BL-563.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useSyncExternalStore,
    type ReactNode,
} from 'react';

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

import { installReconcileTriggers } from '../../stores/realtime/install-reconcile-triggers';
import {
    LedgerStore,
    type LedgerFetchState,
    type LedgerInitialFetcher,
    type LedgerItem,
    type LedgerReconciler,
} from '../../stores/realtime/ledger-store';

export { LedgerStore } from '../../stores/realtime/ledger-store';
export type {
    LedgerCursor,
    LedgerFetchState,
    LedgerFetchStatus,
    LedgerInitialFetcher,
    LedgerItem,
    LedgerReconciler,
} from '../../stores/realtime/ledger-store';

const LedgerStoreContext = createContext<LedgerStore | null>(null);

/**
 * Default periodic safety-net tick (ms). The wake-event listeners
 * (focus / visibilitychange / online / transport-reconnect) cover the
 * common cases; the interval catches the residual "long-visible tab
 * whose websocket never dropped but happened to lose a broadcast" gap.
 * 2 minutes balances staleness against catchup HTTP volume on idle tabs
 * (BL-563 — previously 30s).
 */
const RECONCILE_INTERVAL_MS = 120_000;

export interface LedgerProviderProps {
    /** Transport subscriber the store subscribes against. */
    subscriber: RealtimeSubscriber;
    /**
     * Override the periodic reconcile interval. Tests pass `0` to disable;
     * production code should accept the default.
     */
    reconcileIntervalMs?: number;
    /**
     * Override the reconcile throttle window (BUG-1175). Tests that
     * artificially compress `reconcileIntervalMs` below the throttle window
     * pass `0` to isolate interval wiring; production accepts the default.
     */
    reconcileThrottleMs?: number;
    children: ReactNode;
}

/**
 * Mount once per app. Owns the ledger store AND the global wake/reconnect
 * triggers (window focus, document visibilitychange, window online,
 * transport reconnect, periodic interval).
 */
export function LedgerProvider({
    subscriber,
    reconcileIntervalMs = RECONCILE_INTERVAL_MS,
    reconcileThrottleMs,
    children,
}: LedgerProviderProps) {
    const store = useMemo(() => new LedgerStore(subscriber), [subscriber]);

    useEffect(() => {
        return installReconcileTriggers({
            reconcileAll: () => store.reconcileAll(),
            subscriber,
            intervalMs: reconcileIntervalMs,
            reconcileThrottleMs,
        });
    }, [store, subscriber, reconcileIntervalMs, reconcileThrottleMs]);

    return (
        <LedgerStoreContext.Provider value={store}>
            {children}
        </LedgerStoreContext.Provider>
    );
}

function useLedgerStore(): LedgerStore {
    const store = useContext(LedgerStoreContext);
    if (!store) {
        throw new Error('useLedger must be used inside a <LedgerProvider>');
    }
    return store;
}

export interface UseLedgerOptions<T extends LedgerItem> {
    /** Invoked once by the FIRST subscriber to seed the ledger. */
    initialFetch?: LedgerInitialFetcher<T>;
    /**
     * Invoked on focus / visibilitychange / online / reconnect / interval
     * to close gaps. Should return items strictly after the given cursor
     * (or all items if the cursor is undefined).
     */
    reconcile?: LedgerReconciler<T>;
}

/**
 * Subscribe to the append-only ledger at `topic`. Returns the items
 * ascending by `(createdAt, id)`. Multiple consumers of the same topic
 * share a single underlying transport subscription and the same item
 * list (dedupe by id, cursor-based catch-up on reconcile triggers).
 *
 * The returned array reference is stable across renders when no new
 * items were appended — `useMemo` over the hook output behaves correctly.
 *
 * @param topic - Built via the `topics` helper; must end in `:ledger`.
 */
export function useLedger<T extends LedgerItem>(
    topic: string,
    options?: UseLedgerOptions<T>,
): readonly T[] {
    const store = useLedgerStore();

    // Pin the callbacks so subscribe() identity stays stable per topic.
    const initialFetchRef = useRef(options?.initialFetch);
    initialFetchRef.current = options?.initialFetch;
    const reconcileRef = useRef(options?.reconcile);
    reconcileRef.current = options?.reconcile;

    const subscribe = useCallback(
        (listener: () => void) =>
            store.subscribe<T>(
                topic,
                listener,
                initialFetchRef.current,
                reconcileRef.current,
            ),
        [store, topic],
    );

    const getSnapshot = useCallback(
        () => store.getItems(topic) as readonly T[],
        [store, topic],
    );

    return useSyncExternalStore(
        subscribe,
        getSnapshot,
        () => EMPTY as readonly T[],
    );
}

const EMPTY: readonly LedgerItem[] = Object.freeze([]);

/**
 * Returns a stable callback that runs the topic's reconciler against the
 * current cursor — same code path the provider invokes on focus /
 * visibilitychange / online / reconnect / interval, but exposed for
 * manual "Retry" affordances (e.g. a failed initial-fetch banner with a
 * retry button). Safe to call before the topic has any subscribers — a
 * no-op in that case.
 *
 * The reconciler that runs is the one captured by the FIRST subscriber
 * for the topic, matching the provider's own reconcile triggers.
 */
export function useLedgerReconcile(topic: string): () => Promise<void> {
    const store = useLedgerStore();
    return useCallback(() => store.reconcileTopic(topic), [store, topic]);
}

/**
 * Subscribe to a topic's SHARED initial-fetch state (BUG-803).
 *
 * The store runs `initialFetch` once for the topic's FIRST subscriber, so a
 * later subscriber (e.g. the "All Bidding Activity" modal opening over an inline
 * panel already on the topic) never runs a fetch of its own. Deriving
 * `loaded`/`error` from this shared state — rather than from whether THIS
 * consumer's fetch ran — lets every subscriber settle correctly instead of a
 * late one sitting on a spinner nothing will clear.
 *
 * Returns a stable object reference per change, safe to read every render.
 * Observing fetch state does not affect the item subscription / ref-count.
 *
 * @param topic - Same `:ledger` topic passed to `useLedger`; '' yields the
 *                `loading` default (the consumer typically short-circuits when
 *                the topic is empty / disabled).
 */
export function useLedgerFetchState(topic: string): LedgerFetchState {
    const store = useLedgerStore();
    const subscribe = useCallback(
        (listener: () => void) => store.subscribeFetchState(topic, listener),
        [store, topic],
    );
    const getSnapshot = useCallback(
        () => store.getFetchState(topic),
        [store, topic],
    );
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
